import { Box } from '@mui/material'
import { Trans } from 'react-i18next'
import { ID, Port } from '../../types/models'
import { PortConfig } from '../../types/ports'
import { PortControl } from './PortControl'

/**
 * Render a red warning text when the number of energy meter ports that are
 * marked as part of the building's net metering is not exactly one at the
 * current location. Uses pending config, so the warning responds immediately
 * when the user toggles "Part of building net metering".
 *
 * Returns undefined when the count is exactly one (the expected config).
 */
export function renderNetMeterCountWarning(
  portControl: PortControl<Port, PortConfig>
): React.ReactNode | undefined {
  const locationId = portControl.device.location_id as ID | undefined
  if (locationId === undefined) return undefined

  const meters = portControl.systemControl.ports({
    portType: ['EM_SIMPLE', 'EM_3PH', 'EM_HAN'],
    locationId,
  })
  const activeCount = meters.filter(m => m.isSummer()).length

  if (activeCount === 1) return undefined

  if (activeCount === 0) {
    return (
      <Box sx={{ mt: 1, color: 'error.main' }}>
        <Trans>Warning: Currently no energy meters configured as active building net meters. If this meter is measuring the building total, click "Part of building net metering" ON. If you are planning to add another meter, you can ignore this warning.</Trans>
      </Box>
    )
  }

  return (
    <Box sx={{ mt: 1, color: 'error.main' }}>
      {portControl.app.t('Warning: Currently {count} energy meters configured as active building net meters: using their sum for optimization. Make sure this reflects the reality. If there actually is one single meter for the whole building, turn "Part of the building net metering" OFF from all others except the true (best) meter.', { count: activeCount }).replace('{count}', `${activeCount}`)}
    </Box>
  )
}
